import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Compass,
  Settings,
  TrendingUp,
  Package,
  Database,
  Bell,
  Search,
  ChevronDown,
  LogOut,
  User,
  HelpCircle,
  DollarSign,
  AlertCircle,
  Sparkles,
  FolderOpen,
  Lightbulb,
  Mail,
  SlidersHorizontal,
  MapPin,
  Rocket,
} from "lucide-react";
import { DASHBOARD_SUMMARY } from "../data/mockData";

const tabs = [
  { id: "positioning",label: "Positioning", icon: MapPin },
  { id: "fundmatch",  label: "FundMatch",  icon: Compass },
  { id: "fundreach",  label: "FundReach",  icon: Mail },
  { id: "adoption",   label: "Adoption",   icon: Rocket },
  { id: "adminhub",   label: "AdminHub",   icon: Settings },
  { id: "impactgrow", label: "ImpactGrow", icon: TrendingUp },
  { id: "resourcehub",label: "ResourceHub",icon: Package },
  { id: "dataintel",  label: "DataIntel",  icon: Database },
  { id: "settings",   label: "Settings",   icon: SlidersHorizontal },
];

const SummarySnippets = ({ onTabChange }) => {
  const summary = DASHBOARD_SUMMARY;

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="summary-snippets">
      {/* FundMatch Snippet */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        onClick={() => onTabChange?.("fundmatch")}
        className="bg-white rounded-xl border border-zinc-200 p-4 hover:border-teal-200 hover:shadow-sm transition-all cursor-pointer"
        data-testid="snippet-fundmatch"
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-100 to-teal-100 flex items-center justify-center">
            <DollarSign className="h-4 w-4 text-teal-600" />
          </div>
          <span className="text-xs font-medium text-zinc-500 uppercase tracking-wide">FundMatch</span>
        </div>
        <p className="text-xl font-bold text-zinc-900 font-heading">
          ${(summary.fundMatch.expectedOpportunities / 1000000).toFixed(2)}M
        </p>
        <p className="text-xs text-zinc-500">Expected Opportunities</p>
      </motion.div>

      {/* AdminHub Snippet */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        onClick={() => onTabChange?.("adminhub")}
        className="bg-white rounded-xl border border-zinc-200 p-4 hover:border-amber-200 hover:shadow-sm transition-all cursor-pointer"
        data-testid="snippet-adminhub"
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
            <AlertCircle className="h-4 w-4 text-amber-600" />
          </div>
          <span className="text-xs font-medium text-zinc-500 uppercase tracking-wide">AdminHub</span>
        </div>
        <p className="text-xl font-bold text-zinc-900 font-heading">{summary.adminHub.pendingActions}</p>
        <p className="text-xs text-zinc-500">Pending Actions</p>
      </motion.div>

      {/* ImpactGrow Snippet */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        onClick={() => onTabChange?.("impactgrow")}
        className="bg-white rounded-xl border border-zinc-200 p-4 hover:border-cyan-200 hover:shadow-sm transition-all cursor-pointer"
        data-testid="snippet-impactgrow"
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg bg-cyan-100 flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-cyan-600" />
          </div>
          <span className="text-xs font-medium text-zinc-500 uppercase tracking-wide">ImpactGrow</span>
        </div>
        <p className="text-xl font-bold text-zinc-900 font-heading">{summary.impactGrow.currentLevel}</p>
        <p className="text-xs text-zinc-500 truncate">{summary.impactGrow.nextStep.substring(0, 25)}...</p>
      </motion.div>

      {/* ResourceHub Snippet */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        onClick={() => onTabChange?.("resourcehub")}
        className="bg-white rounded-xl border border-zinc-200 p-4 hover:border-violet-200 hover:shadow-sm transition-all cursor-pointer"
        data-testid="snippet-resourcehub"
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
            <FolderOpen className="h-4 w-4 text-violet-600" />
          </div>
          <span className="text-xs font-medium text-zinc-500 uppercase tracking-wide">ResourceHub</span>
        </div>
        <p className="text-xl font-bold text-zinc-900 font-heading">{summary.resourceHub.newResources}</p>
        <p className="text-xs text-zinc-500">New Resources</p>
      </motion.div>

      {/* DataIntel Snippet */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        onClick={() => onTabChange?.("dataintel")}
        className="bg-white rounded-xl border border-zinc-200 p-4 hover:border-blue-200 hover:shadow-sm transition-all cursor-pointer"
        data-testid="snippet-dataintel"
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
            <Lightbulb className="h-4 w-4 text-blue-600" />
          </div>
          <span className="text-xs font-medium text-zinc-500 uppercase tracking-wide">DataIntel</span>
        </div>
        <p className="text-xl font-bold text-zinc-900 font-heading">{summary.dataIntel.newInsights}</p>
        <p className="text-xs text-zinc-500">New Insights</p>
      </motion.div>
    </div>
  );
};

export const DashboardLayout = ({ children, activeTab, onTabChange, user, onLogout }) => {
  const navigate = useNavigate();
  const initials = user?.full_name
    ? user.full_name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    : 'IP';
  const displayName = user?.full_name || 'Your Organization';
  const displayEmail = user?.email || '';

  const handleSignOut = () => {
    if (onLogout) onLogout();
    else navigate('/');
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Top Navigation */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-50" data-testid="dashboard-header">
        <div className="px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="dashboard-logo">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-400 to-teal-500 flex items-center justify-center shadow-sm">
                <span className="text-white font-bold text-sm">IP</span>
              </div>
              <span className="font-heading font-bold text-lg text-zinc-900 hidden sm:inline">Impact Plexus</span>
            </Link>

            {/* Search */}
            <div className="hidden md:flex items-center flex-1 max-w-md mx-8">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                <input
                  type="text"
                  placeholder="Search grants, funders, resources..."
                  className="w-full pl-10 pr-4 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  data-testid="dashboard-search"
                />
              </div>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                <Bell className="h-5 w-5 text-zinc-600" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 px-2" data-testid="user-menu">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-gradient-to-br from-slate-100 to-teal-100 text-teal-700 text-sm font-medium">{initials}</AvatarFallback>
                    </Avatar>
                    <ChevronDown className="h-4 w-4 text-zinc-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2 border-b border-zinc-100">
                    <p className="font-medium text-sm text-zinc-900">{displayName}</p>
                    <p className="text-xs text-zinc-500">{displayEmail}</p>
                  </div>
                  <DropdownMenuItem className="cursor-pointer" onClick={() => onTabChange("settings")}>
                    <User className="mr-2 h-4 w-4" />
                    Profile Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem className="cursor-pointer">
                    <HelpCircle className="mr-2 h-4 w-4" />
                    Help & Support
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="cursor-pointer text-red-600" onClick={handleSignOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 md:px-6 lg:px-8 py-6">
        {/* Summary Snippets */}
        <SummarySnippets onTabChange={onTabChange} />

        {/* Tab Navigation */}
        <Tabs value={activeTab} onValueChange={onTabChange} className="mb-6" data-testid="dashboard-tabs">
          <TabsList className="bg-white border border-zinc-200 p-1 h-auto flex-wrap">
            {tabs.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="flex items-center gap-2 px-4 py-2.5 data-[state=active]:bg-gradient-to-r data-[state=active]:from-slate-50 data-[state=active]:to-teal-50 data-[state=active]:text-teal-700 data-[state=active]:shadow-none"
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="h-4 w-4" />
                <span className="hidden sm:inline font-medium">{tab.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
          {/* Classic View link — lets users compare v1 campaign system vs new agentic system */}
          <div className="mt-2 text-right">
            <Link
              to="/dashboardv1"
              className="text-xs text-zinc-400 hover:text-zinc-600 transition-colors"
              title="Compare with classic campaign view"
            >
              Classic View ↗
            </Link>
          </div>
        </Tabs>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          {children}
        </motion.div>
      </main>
    </div>
  );
};

export default DashboardLayout;
